/* Wires up the Relewise toolbar on collection + search templates.

   Responsibilities:
     1) Toggles `body.relewise-filters-open` from the Filter button.
     2) Mirrors Relewise's "products shown" count into the toolbar.
     3) Restructures the Relewise component's UI so sort + facets sit
        in a single sidebar column above the product grid.
     4) Replaces the native sort <select> with a custom radio-list
        accordion ("SORT BY") at the top of the sidebar; bridges
        radio changes back to the underlying <select>.

   DOM layouts (verified):
     PLP (test2): facets + sort live in the LIGHT DOM as light-DOM
       children of <relewise-product-search>.
     /search:    facets + sort live INSIDE <relewise-product-search>'s
       shadow root. Document CSS doesn't reach them, so we inject
       a stylesheet via adoptedStyleSheets and bridge state through
       CSS custom properties (which inherit across shadow boundaries).

   The facet *items* (<relewise-checklist-string-value-facet> etc.)
   live inside <relewise-facets>'s OWN shadow root — they expose
   `part="title|input|label|value|hits"`. To style them we inject
   a second stylesheet into the relewise-facets shadow root.
*/
(function () {
  'use strict';

  var FILTER_TOGGLE_SELECTOR = '[data-collection-filter-toggle]';
  var COUNT_SLOT_SELECTOR = '[data-collection-product-count]';
  var SIDEBAR_CLASS = 'collection-relewise-sidebar';
  var SHADOW_FLAG = 'data-rw-toolbar-injected';
  var FACETS_SHADOW_FLAG = 'data-rw-facets-injected';
  var SORT_ACCORDION_CLASS = 'rw-sort-accordion';
  var DEBUG = false;

  function log() {
    if (!DEBUG) return;
    try { console.log.apply(console, ['[relewise-toolbar]'].concat([].slice.call(arguments))); } catch (_) {}
  }

  /* ── Relewise text localization ──────────────────
     Relewise's Product Search renders "Showing X out of Y products",
     "Load More", and (on /search) "N results" in English regardless of
     the storefront locale — its only translation hook is the app-side
     useSearch({ localization }) config, which the theme doesn't control.
     So we translate the word-slots in place.

     The text lives in nested shadow roots, so we walk every shadow root
     and rewrite the relevant text nodes. Relewise builds the strings the
     same slot-based way we translate them (showing / out of / products /
     Load More), so word-for-word replacement preserves the numbers'
     positions. We edit individual text nodes (never .textContent) so we
     don't drop the Lit binding markers the component re-renders against.
     Replacements are idempotent — once a slot is translated its English
     token is gone — so re-runs on re-render are no-ops. */

  var RW_STRINGS = {
    en: { showing: 'Showing',   outOf: 'out of', products: 'products',  loadMore: 'Load More',   results: 'results',    sortBy: 'Sort by'        },
    sv: { showing: 'Visar',     outOf: 'av',     products: 'produkter', loadMore: 'Visa fler',   results: 'resultat',   sortBy: 'Sortera efter'  },
    nb: { showing: 'Viser',     outOf: 'av',     products: 'produkter', loadMore: 'Vis mer',     results: 'resultater', sortBy: 'Sorter etter'   },
    da: { showing: 'Viser',     outOf: 'af',     products: 'produkter', loadMore: 'Vis mere',    results: 'resultater', sortBy: 'Sortér efter'   },
    fi: { showing: 'Näytetään', outOf: '/',      products: 'tuotetta',  loadMore: 'Näytä lisää', results: 'tulosta',    sortBy: 'Lajittele'      }
  };

  function rwDetectLocale() {
    var l = '';
    try { if (window.Shopify && window.Shopify.locale) l = window.Shopify.locale; } catch (_) {}
    if (!l) l = document.documentElement.getAttribute('lang') || '';
    l = (l || 'en').toLowerCase().split('-')[0];
    if (l === 'no' || l === 'nn') l = 'nb'; // normalize Norwegian variants
    return l;
  }

  var RW_T = RW_STRINGS[rwDetectLocale()] || RW_STRINGS.en;
  var RW_NEEDS_LOCALIZE = RW_T !== RW_STRINGS.en;

  function localizeRwText(text) {
    if (!text) return text;
    return text
      .replace(/\bLoad More\b/g, RW_T.loadMore)
      .replace(/\bShowing\b/g, RW_T.showing)
      .replace(/\bout of\b/g, RW_T.outOf)
      .replace(/\bproducts\b/g, RW_T.products)
      .replace(/\bresults?\b/gi, RW_T.results);
  }

  var RW_TARGET_SELECTORS = '.rw-products-shown, .rw-load-more-text, .rw-results-text';

  // Rewrite each text node within a matched element (never textContent —
  // that would drop the Lit markers the component re-renders against).
  function localizeRwElement(el) {
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null, false);
    var node;
    while ((node = walker.nextNode())) {
      var next = localizeRwText(node.nodeValue || '');
      if (next !== node.nodeValue) node.nodeValue = next;
    }
  }

  // Collect a root plus every shadow root nested beneath it.
  function collectRwRoots(root, acc) {
    acc.push(root);
    var hosts = root.querySelectorAll ? root.querySelectorAll('*') : [];
    for (var i = 0; i < hosts.length; i++) {
      if (hosts[i].shadowRoot) collectRwRoots(hosts[i].shadowRoot, acc);
    }
    return acc;
  }

  var rwObservedRoots = (typeof WeakSet !== 'undefined') ? new WeakSet() : null;
  var rwScheduled = false;

  function rwScheduleLocalize() {
    if (rwScheduled) return;
    rwScheduled = true;
    requestAnimationFrame(function () { rwScheduled = false; localizeAndObserveRw(); });
  }

  function localizeAndObserveRw() {
    var roots = collectRwRoots(document, []);
    for (var i = 0; i < roots.length; i++) {
      var r = roots[i];
      var targets = r.querySelectorAll ? r.querySelectorAll(RW_TARGET_SELECTORS) : [];
      for (var t = 0; t < targets.length; t++) localizeRwElement(targets[t]);
      // Observe each root once so we re-localize when Relewise re-renders
      // (e.g. after a "Load More" click resets the text to English).
      if (rwObservedRoots && !rwObservedRoots.has(r)) {
        rwObservedRoots.add(r);
        try {
          new MutationObserver(rwScheduleLocalize)
            .observe(r, { childList: true, subtree: true, characterData: true });
        } catch (_) {}
      }
    }
  }

  function wireRelewiseLocalization() {
    if (!RW_NEEDS_LOCALIZE) return; // English storefront: nothing to translate.
    localizeAndObserveRw();
    // Backstops for components that hydrate after first paint.
    setTimeout(localizeAndObserveRw, 500);
    setTimeout(localizeAndObserveRw, 1500);
    setTimeout(localizeAndObserveRw, 3000);
  }

  /* ── Filter toggle ─────────────────────────────── */

  function wireFilterToggle() {
    var btn = document.querySelector(FILTER_TOGGLE_SELECTOR);
    if (!btn) { log('toggle button not found'); return; }
    btn.addEventListener('click', function () {
      var open = document.body.classList.toggle('relewise-filters-open');
      btn.setAttribute('aria-expanded', String(open));
      log('toggled', { open: open });
    });
    log('toggle wired');
  }

  /* ── Shadow-DOM-aware traversal ──────────────────── */

  function deepFind(root, selector) {
    if (!root) return null;
    var direct = root.querySelector(selector);
    if (direct) return { el: direct, root: root };
    var all = root.querySelectorAll('*');
    for (var i = 0; i < all.length; i++) {
      var sr = all[i].shadowRoot;
      if (sr) {
        var hit = deepFind(sr, selector);
        if (hit) return hit;
      }
    }
    return null;
  }

  /* ── CSS injected into the shadow root that owns the
        result-container + facets + sort (search variant). ── */
  var SHADOW_CSS = [
    /* Native sort row + duplicated count: hidden everywhere. */
    '.rw-sorting-button-container,',
    '.rw-sorting-container,',
    '.rw-results-text {',
    '  display: none !important;',
    '}',

    /* Hide the internal <relewise-search-bar> that <relewise-product-search>
       renders inside its own shadow root on /search. The header already
       owns the site\'s search input, so this internal one leaks visible
       artifacts (its bottom border line + a magnifier icon that floats
       into the result area). relewise-search.css has a light-DOM
       selector for the same purpose but it can\'t reach in here. */
    'relewise-search-bar {',
    '  display: none !important;',
    '}',

    /* Hide native sort component (we render radios above). Use clip
       so the underlying <select> remains in DOM for value sync. */
    'relewise-product-search-sorting {',
    '  position: absolute !important;',
    '  width: 1px !important;',
    '  height: 1px !important;',
    '  overflow: hidden !important;',
    '  clip: rect(0 0 0 0) !important;',
    '  pointer-events: none !important;',
    '}',

    /* Facet panel + sidebar wrapper visibility, driven by
       --rw-filters-display (set on body.relewise-filters-open). */
    'relewise-facets {',
    '  display: var(--rw-filters-display, none) !important;',
    '  background: transparent !important;',
    '  padding: 0 !important;',
    '  border: none !important;',
    '  width: 100%;',
    '}',

    '.' + SIDEBAR_CLASS + ' {',
    '  display: var(--rw-filters-display, none) !important;',
    '  flex-direction: column;',
    '  gap: 16px;',
    '  width: 100%;',
    '}',

    /* Result-container layout. */
    '.result-container {',
    '  display: grid !important;',
    '  grid-template-columns: var(--rw-result-cols, 1fr) !important;',
    '  gap: var(--rw-result-gap, 0) !important;',
    '  align-items: start !important;',
    '}',

    /* Sort accordion + radio list (rendered by JS into the same
       shadow tree). */
    '.' + SORT_ACCORDION_CLASS + ' {',
    '  display: block;',
    '  border-bottom: 1px solid #E6E4DC;',
    '  padding: 0;',
    '  margin: 0;',
    '}',
    '.' + SORT_ACCORDION_CLASS + '[open] > summary .rw-accordion-chevron { transform: rotate(180deg); }',
    '.' + SORT_ACCORDION_CLASS + ' > summary {',
    '  list-style: none;',
    '  display: flex;',
    '  align-items: center;',
    '  justify-content: space-between;',
    '  padding: 16px 0;',
    '  cursor: pointer;',
    '  font-family: "Euclid Circular A", "Inter", -apple-system, sans-serif;',
    '  font-size: 13px;',
    '  font-weight: 600;',
    '  letter-spacing: 0.08em;',
    '  text-transform: uppercase;',
    '  color: #1C1714;',
    '  user-select: none;',
    '}',
    '.' + SORT_ACCORDION_CLASS + ' > summary::-webkit-details-marker { display: none; }',
    '.rw-accordion-chevron {',
    '  width: 12px;',
    '  height: 12px;',
    '  flex-shrink: 0;',
    '  transition: transform 180ms ease;',
    '  color: #9B9690;',
    '}',
    '.rw-sort-options {',
    '  display: flex;',
    '  flex-direction: column;',
    '  gap: 2px;',
    '  padding: 0 0 16px 0;',
    '}',
    '.rw-sort-option {',
    '  display: flex;',
    '  align-items: center;',
    '  gap: 12px;',
    '  cursor: pointer;',
    '  font-family: "Euclid Circular A", "Inter", -apple-system, sans-serif;',
    '  font-size: 13px;',
    '  color: #1C1714;',
    '  padding: 2px 0;',
    '}',
    '.rw-sort-option input[type="radio"] {',
    '  position: absolute;',
    '  opacity: 0;',
    '  width: 0;',
    '  height: 0;',
    '  pointer-events: none;',
    '}',
    '.rw-sort-radio {',
    '  width: 18px;',
    '  height: 18px;',
    '  border: 1.5px solid #D1D3D4;',
    '  border-radius: 50%;',
    '  flex-shrink: 0;',
    '  display: inline-flex;',
    '  align-items: center;',
    '  justify-content: center;',
    '  transition: border-color 180ms ease;',
    '  box-sizing: border-box;',
    '}',
    '.rw-sort-option input[type="radio"]:checked ~ .rw-sort-radio { border-color: #1C1714; }',
    '.rw-sort-option input[type="radio"]:checked ~ .rw-sort-radio::after {',
    '  content: "";',
    '  width: 10px;',
    '  height: 10px;',
    '  background: #1C1714;',
    '  border-radius: 50%;',
    '}'
  ].join('\n');

  /* CSS injected into <relewise-facets>'s own shadow root so we can
     style its child facet cards (which expose ::part hooks but live
     one shadow boundary deeper). */
  var FACETS_SHADOW_CSS = [
    ':host {',
    '  background: transparent !important;',
    '  padding: 0 !important;',
    '  border: none !important;',
    '  width: 100%;',
    '  display: block;',
    '}',
    '.rw-facets-container {',
    '  background: transparent !important;',
    '  border: none !important;',
    '  padding: 0 !important;',
    '  display: flex !important;',
    '  flex-direction: column !important;',
    '  gap: 0 !important;',
    '}',
    /* Strip the native filter-toggle button (we have our own). */
    '.rw-facet-button { display: none !important; }',
    /* Each facet card: flat, hairline divider between sections. */
    'relewise-checklist-string-value-facet,',
    'relewise-checklist-double-value-facet,',
    'relewise-checklist-boolean-value-facet,',
    'relewise-double-range-facet,',
    'relewise-sales-price-range-facet,',
    'relewise-category-facet,',
    'relewise-category-hierarchy-facet {',
    '  display: block;',
    '  background: transparent !important;',
    '  border: none !important;',
    '  border-bottom: 1px solid #E6E4DC !important;',
    '  border-radius: 0 !important;',
    /* Breathing room below the last option before the divider — matches the
       sort list (.rw-sort-options padding-bottom: 16px). */
    '  padding: 0 0 16px 0 !important;',
    '  margin: 0 !important;',
    '}',
    'relewise-checklist-string-value-facet::part(container),',
    'relewise-checklist-double-value-facet::part(container),',
    'relewise-checklist-boolean-value-facet::part(container),',
    'relewise-double-range-facet::part(container),',
    'relewise-sales-price-range-facet::part(container),',
    'relewise-category-facet::part(container),',
    'relewise-category-hierarchy-facet::part(container) {',
    '  background: transparent !important;',
    '  border: none !important;',
    '  padding: 0 !important;',
    '  margin: 0 !important;',
    '}',
    'relewise-checklist-string-value-facet::part(title),',
    'relewise-checklist-double-value-facet::part(title),',
    'relewise-checklist-boolean-value-facet::part(title),',
    'relewise-double-range-facet::part(title),',
    'relewise-sales-price-range-facet::part(title),',
    'relewise-category-facet::part(title),',
    'relewise-category-hierarchy-facet::part(title) {',
    '  font-family: "Euclid Circular A", "Inter", -apple-system, sans-serif !important;',
    '  font-size: 13px !important;',
    '  font-weight: 600 !important;',
    '  letter-spacing: 0.08em !important;',
    '  text-transform: uppercase !important;',
    '  color: #1C1714 !important;',
    '  margin: 0 !important;',
    /* Tighter gap between the facet title and its options (was 12px). */
    '  padding: 16px 0 8px 0 !important;',
    '  border: none !important;',
    '  background: transparent !important;',
    '}',
    'relewise-checklist-string-value-facet::part(label),',
    'relewise-checklist-string-value-facet::part(value),',
    'relewise-checklist-double-value-facet::part(label),',
    'relewise-checklist-double-value-facet::part(value),',
    'relewise-checklist-boolean-value-facet::part(label),',
    'relewise-checklist-boolean-value-facet::part(value) {',
    '  font-family: "Euclid Circular A", "Inter", -apple-system, sans-serif !important;',
    /* Match the SORT options (--t-small = 13px). */
    '  font-size: 13px !important;',
    '  color: #1C1714 !important;',
    '  white-space: normal !important;',
    '  overflow-wrap: break-word !important;',
    '}',
    /* Match the sort options\' vertical rhythm — ~6px between rows (sort
       uses padding 2px + flex gap 2px + padding 2px). Relewise spaces its
       rows with margin on .rw-label, but adjacent margins collapse through
       the wrapper <div>, halving the gap. Use padding (no collapse) instead. */
    'relewise-checklist-string-value-facet::part(label),',
    'relewise-checklist-double-value-facet::part(label),',
    'relewise-checklist-boolean-value-facet::part(label) {',
    '  margin-top: 0 !important;',
    '  margin-bottom: 0 !important;',
    '  padding-top: 3px !important;',
    '  padding-bottom: 3px !important;',
    '  line-height: 1.4 !important;',
    '}',
    'relewise-checklist-string-value-facet::part(hits),',
    'relewise-checklist-double-value-facet::part(hits),',
    'relewise-checklist-boolean-value-facet::part(hits) {',
    '  color: #6E6E6E !important;',
    '  font-family: "JetBrains Mono", "SFMono-Regular", Menlo, monospace !important;',
    '  font-size: 11px !important;',
    '}',
    'relewise-checklist-string-value-facet::part(input),',
    'relewise-checklist-double-value-facet::part(input),',
    'relewise-checklist-boolean-value-facet::part(input) {',
    '  accent-color: #1C1714 !important;',
    '}'
  ].join('\n');

  function injectStyle(shadowRoot, cssText, flag) {
    if (!shadowRoot) return;
    if (shadowRoot[flag]) return;
    try {
      var sheet = new CSSStyleSheet();
      sheet.replaceSync(cssText);
      shadowRoot.adoptedStyleSheets = (shadowRoot.adoptedStyleSheets || []).concat([sheet]);
      shadowRoot[flag] = true;
      return;
    } catch (_) { /* fall through to <style> */ }
    if (!shadowRoot.querySelector('style[' + flag + ']')) {
      var style = document.createElement('style');
      style.setAttribute(flag, '');
      style.textContent = cssText;
      shadowRoot.appendChild(style);
      shadowRoot[flag] = true;
    }
  }

  /* ── Sort radio accordion ──────────────────────────
     Builds <details><summary>SORT BY</summary>... radios ...</details>
     mirrored to the native <select> inside <relewise-product-search-sorting>. */

  function findNativeSortSelect(sortHost) {
    if (!sortHost) return null;
    if (sortHost.shadowRoot) {
      var sel = sortHost.shadowRoot.querySelector('select');
      if (sel) return sel;
    }
    return sortHost.querySelector('select');
  }

  function buildSortAccordion(sortHost, ownerDoc) {
    var select = findNativeSortSelect(sortHost);
    if (!select || !select.options || select.options.length === 0) return null;

    ownerDoc = ownerDoc || document;
    var details = ownerDoc.createElement('details');
    details.className = SORT_ACCORDION_CLASS;
    details.open = true;

    var summary = ownerDoc.createElement('summary');
    var titleSpan = ownerDoc.createElement('span');
    titleSpan.textContent = RW_T.sortBy || RW_STRINGS.en.sortBy;
    titleSpan.style.cssText = 'text-transform:uppercase;';
    summary.appendChild(titleSpan);
    var chevron = ownerDoc.createElementNS('http://www.w3.org/2000/svg', 'svg');
    chevron.setAttribute('class', 'rw-accordion-chevron');
    chevron.setAttribute('viewBox', '0 0 12 12');
    chevron.setAttribute('fill', 'none');
    var path = ownerDoc.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M2 4.5L6 8.5L10 4.5');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1.5');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    chevron.appendChild(path);
    summary.appendChild(chevron);
    details.appendChild(summary);

    var list = ownerDoc.createElement('div');
    list.className = 'rw-sort-options';
    details.appendChild(list);

    function render() {
      list.innerHTML = '';
      Array.prototype.forEach.call(select.options, function (opt) {
        var label = ownerDoc.createElement('label');
        label.className = 'rw-sort-option';
        var radio = ownerDoc.createElement('input');
        radio.type = 'radio';
        radio.name = 'rw-sort-' + Math.random().toString(36).slice(2, 7);
        radio.value = opt.value;
        radio.checked = opt.value === select.value;
        radio.addEventListener('change', function () {
          if (!radio.checked) return;
          select.value = opt.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        });
        var dot = ownerDoc.createElement('span');
        dot.className = 'rw-sort-radio';
        var text = ownerDoc.createElement('span');
        text.className = 'rw-sort-text';
        text.textContent = opt.textContent || opt.label || opt.value;
        label.appendChild(radio);
        label.appendChild(dot);
        label.appendChild(text);
        list.appendChild(label);
      });
    }
    render();

    /* Re-render when Relewise rebuilds <option>s. */
    var optionObserver = new MutationObserver(render);
    optionObserver.observe(select, { childList: true, subtree: true, attributes: true, attributeFilter: ['value'] });

    /* External value sync (e.g. URL param). */
    select.addEventListener('change', function () {
      Array.prototype.forEach.call(list.querySelectorAll('input[type="radio"]'), function (input) {
        input.checked = input.value === select.value;
      });
    });

    return details;
  }

  /* ── Force facets always-expanded (kill the mobile button) ──
     <relewise-facets> initializes its `showFacets` state to
     `window.innerWidth >= 1024`, and BELOW 1024px it renders the
     facet list (`.rw-facets-container`) only when that state is true —
     otherwise the list isn't in the DOM at all (Lit `nothing`). The
     only thing that flips the state is the `.rw-facet-button`, which we
     hide (our toolbar owns the toggle). Net effect on mobile/tablet:
     no button to press, so the facets never mount and only SORT shows.

     CSS can't fix this (you can't `display:block` an element that was
     never rendered). Instead, when the container is absent we click the
     hidden button to flip `showFacets` on — mounting the list. Guarded
     on the container's absence so we never toggle it back off, and
     re-checked by the same MutationObserver that drives ensureSidebar,
     so it self-heals if Relewise re-renders and collapses again. */
  function ensureFacetsExpanded(facets) {
    var sr = facets.shadowRoot;
    if (!sr) return;
    if (sr.querySelector('.rw-facets-container')) return; // already open
    var btn = sr.querySelector('.rw-facet-button');
    if (btn) { log('expanding facets via hidden button'); btn.click(); }
  }

  /* ── Sidebar reordering ──────────────────────────── */

  function ensureSidebar() {
    var facetsFound = deepFind(document, 'relewise-facets');
    var sortFound = deepFind(document, 'relewise-product-search-sorting');
    if (!facetsFound || !sortFound) {
      log('sidebar not yet possible', {
        facetsFound: !!facetsFound,
        sortFound: !!sortFound
      });
      return false;
    }

    var facets = facetsFound.el;
    var sort = sortFound.el;
    var facetsRoot = facetsFound.root;

    /* Outer shadow CSS for layout + sort accordion styling. */
    if (facetsRoot !== document) injectStyle(facetsRoot, SHADOW_CSS, SHADOW_FLAG);
    /* Inner shadow CSS so part-based facet styling reaches each card. */
    if (facets.shadowRoot) injectStyle(facets.shadowRoot, FACETS_SHADOW_CSS, FACETS_SHADOW_FLAG);

    /* Mount the facet list on mobile/tablet (it's button-gated below 1024px). */
    ensureFacetsExpanded(facets);

    var anchor = facets.parentElement;
    if (!anchor) { log('facets has no parent'); return false; }

    /* The sidebar wrapper sits in the same root as facets. */
    var rootEl = (facetsRoot === document) ? document : facetsRoot;
    var existing = rootEl.querySelector('.' + SIDEBAR_CLASS);
    if (!existing) {
      existing = (rootEl === document ? document : facetsRoot.host.ownerDocument).createElement('div');
      existing.className = SIDEBAR_CLASS;
      anchor.insertBefore(existing, facets);
    }
    if (sort.parentElement !== existing) existing.appendChild(sort);
    if (facets.parentElement !== existing) existing.appendChild(facets);

    /* Build (or rebuild) the sort radio accordion at the top of the
       sidebar, before <relewise-facets>. */
    var existingAccordion = existing.querySelector('.' + SORT_ACCORDION_CLASS);
    if (!existingAccordion) {
      var ownerDoc = (rootEl === document) ? document : facetsRoot.host.ownerDocument;
      var accordion = buildSortAccordion(sort, ownerDoc);
      if (accordion) existing.insertBefore(accordion, facets);
    }

    log('sidebar wrapped', {
      inShadow: facetsRoot !== document,
      anchor: anchor.tagName + (anchor.className ? '.' + String(anchor.className).split(' ').join('.') : '')
    });
    return true;
  }

  function wireSidebar() {
    var attempts = 0;
    var MAX_ATTEMPTS = 80;
    var observer = null;

    var poll = setInterval(function () {
      attempts++;
      if (ensureSidebar()) {
        clearInterval(poll);
        observer = new MutationObserver(function () { ensureSidebar(); });
        observer.observe(document.body, { childList: true, subtree: true });
      } else if (attempts >= MAX_ATTEMPTS) {
        clearInterval(poll);
        log('sidebar wrap timed out');
      }
    }, 250);
  }

  /* ── Count mirror ──────────────────────────────── */

  function findCountEl() {
    var classCandidates = [
      '.rw-results-text',
      '.rw-products-shown',
      '[class*="results-text"]',
      '[class*="products-shown"]'
    ];
    for (var i = 0; i < classCandidates.length; i++) {
      var hit = deepFind(document, classCandidates[i]);
      if (hit) return hit.el;
    }
    return null;
  }

  function syncCount(slot, sourceEl) {
    if (!slot || !sourceEl) return;
    var text = (sourceEl.textContent || '').trim();
    // The source may not be localized yet when we first copy it, so run the
    // same locale-aware swap here too (idempotent) — keeps the toolbar count
    // in the storefront language regardless of timing.
    text = localizeRwText(text);
    slot.textContent = text;
  }

  function wireCountMirror() {
    var slot = document.querySelector(COUNT_SLOT_SELECTOR);
    if (!slot) return;
    var attempts = 0;
    var MAX_ATTEMPTS = 80;
    var poll = setInterval(function () {
      attempts++;
      var sourceEl = findCountEl();
      if (sourceEl) {
        clearInterval(poll);
        syncCount(slot, sourceEl);
        var observer = new MutationObserver(function () { syncCount(slot, sourceEl); });
        observer.observe(sourceEl, { childList: true, characterData: true, subtree: true });
        log('count mirror wired', { source: sourceEl.tagName + '.' + (sourceEl.className || '') });
      } else if (attempts >= MAX_ATTEMPTS) {
        clearInterval(poll);
        log('count mirror timed out');
      }
    }, 250);
  }

  /* ── Internal search-bar hide ────────────────────
     <relewise-product-search> renders its own internal <relewise-search-bar>
     inside a shadow root that document CSS can't reach, and that we can't
     pinpoint reliably from one specific outer-shadow stylesheet (depth and
     position vary by Relewise version). The header overlay uses the same
     element type, so we can't blanket-hide it either. Walk every shadow
     root, find each <relewise-search-bar>, climb its ancestor chain across
     shadow boundaries, and inline-style display:none on it only when it
     sits inside a <relewise-product-search> (page block) and NOT inside a
     <relewise-product-search-overlay> (header). Inline style wins the
     cascade unconditionally. */

  function searchBarBelongsToPageBlock(el) {
    var node = el;
    while (node && node !== document) {
      var tag = node.tagName;
      if (tag === 'RELEWISE-PRODUCT-SEARCH-OVERLAY') return false;
      if (tag === 'RELEWISE-PRODUCT-SEARCH') return true;
      var parent = node.parentNode;
      if (!parent || parent === document) {
        var root = node.getRootNode && node.getRootNode();
        if (root && root.host) { node = root.host; continue; }
        return false;
      }
      node = parent;
    }
    return false;
  }

  function hideInternalSearchBars(root) {
    if (!root || !root.querySelectorAll) return;
    var bars = root.querySelectorAll('relewise-search-bar');
    for (var i = 0; i < bars.length; i++) {
      var bar = bars[i];
      if (searchBarBelongsToPageBlock(bar)) {
        bar.style.display = 'none';
      }
    }
    var all = root.querySelectorAll('*');
    for (var j = 0; j < all.length; j++) {
      if (all[j].shadowRoot) hideInternalSearchBars(all[j].shadowRoot);
    }
  }

  function wireInternalSearchBarHide() {
    hideInternalSearchBars(document);
    var scheduled = false;
    new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(function () {
        scheduled = false;
        hideInternalSearchBars(document);
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
    // A couple of late re-runs for components that populate asynchronously.
    setTimeout(function () { hideInternalSearchBars(document); }, 500);
    setTimeout(function () { hideInternalSearchBars(document); }, 1500);
  }

  function init() {
    wireFilterToggle();
    wireSidebar();
    wireCountMirror();
    wireInternalSearchBarHide();
    wireRelewiseLocalization();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
